import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Building2 } from "lucide-react";

/**
 * Company / Organisation profile.
 *
 * Two entity modes:
 *   • company (default) — GST-liable business. GSTIN + HSN + GST rate drive invoices.
 *   • ngo               — legacy 80G / 12A pathway.
 *
 * Fields are grouped visually so the same form works for either mode.
 */
const SECTIONS = [
  {
    title: "Legal identity",
    fields: [
      { k: "company_type", label: "Entity Type *", options: [
          { v: "company", l: "Company (GST-liable)" },
          { v: "ngo",     l: "NGO / Trust / Society (80G)" },
      ] },
      { k: "legal_name", label: "Legal / Registered Name *", placeholder: "e.g. HTP Sports Hub Pvt Ltd" },
      { k: "pan", label: "PAN", placeholder: "AAACS1234X" },
      { k: "cin", label: "CIN (Company Identification Number)", placeholder: "U74999DL2024PTC000000", showIf: (f) => f.company_type === "company" },
      { k: "trust_type", label: "Trust Type", placeholder: "Trust / Society / Section 8", showIf: (f) => f.company_type === "ngo" },
    ],
  },
  {
    title: "GST & invoicing (Company mode)",
    showIf: (f) => f.company_type === "company",
    fields: [
      { k: "gstin", label: "GSTIN (15-digit) *", placeholder: "07AABCS1234H1Z5" },
      { k: "hsn_default", label: "Default HSN / SAC", placeholder: "999652 — sports & recreation" },
      { k: "gst_rate", label: "GST Rate %", type: "number", placeholder: "18" },
      { k: "gst_inclusive", label: "Displayed prices already include GST?", type: "toggle" },
      { k: "invoice_prefix", label: "Invoice number prefix", placeholder: "SSH-INV/25-26/" },
      { k: "next_invoice_no", label: "Next invoice number", type: "number", placeholder: "1" },
    ],
  },
  {
    title: "Slot booking · deposit / partial payment",
    fields: [
      { k: "booking_partial_enabled", label: "Allow guests to pay a deposit at booking?", type: "toggle" },
      { k: "booking_deposit_percent", label: "Minimum deposit %", type: "number", placeholder: "25" },
    ],
  },
  {
    title: "80G / 12A (NGO mode)",
    showIf: (f) => f.company_type === "ngo",
    fields: [
      { k: "tan", label: "TAN", placeholder: "" },
      { k: "section_12a_reg", label: "12A Registration No." },
      { k: "section_80g_reg", label: "80G Registration No." },
      { k: "section_80g_valid_from", label: "80G Valid From", placeholder: "YYYY-MM-DD" },
      { k: "section_80g_valid_till", label: "80G Valid Till", placeholder: "YYYY-MM-DD" },
      { k: "receipt_prefix", label: "80G receipt prefix", placeholder: "SSH/25-26/" },
      { k: "next_receipt_no", label: "Next receipt no.", type: "number", placeholder: "1" },
    ],
  },
  {
    title: "Contact & address",
    fields: [
      { k: "address", label: "Registered Address *", placeholder: "Full street address", wide: true },
      { k: "city", label: "City" },
      { k: "state", label: "State" },
      { k: "pincode", label: "PIN Code" },
      { k: "phone", label: "Contact Phone" },
      { k: "email", label: "Contact Email" },
      { k: "website", label: "Website" },
    ],
  },
  {
    title: "Bank details (invoice footer)",
    fields: [
      { k: "bank_name", label: "Bank Name" },
      { k: "bank_account", label: "Bank Account No." },
      { k: "bank_ifsc", label: "IFSC" },
      { k: "bank_upi", label: "UPI ID" },
      { k: "authorized_signatory", label: "Authorized Signatory (name)" },
      { k: "authorized_signatory_role", label: "Signatory Role", placeholder: "Director / Owner / Trustee" },
    ],
  },
];

export default function AdminNgoProfile() {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);
  const load = async () => {
    try { const { data } = await api.get("/admin/ngo-profile"); setForm(data || {}); }
    catch (e) { toast.error(apiError(e)); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      ["next_receipt_no", "next_invoice_no"].forEach((k) => {
        if (payload[k] !== undefined && payload[k] !== "") payload[k] = parseInt(payload[k]) || 1;
      });
      if (payload.gst_rate !== undefined && payload.gst_rate !== "") payload.gst_rate = parseFloat(payload.gst_rate) || 18;
      if (payload.booking_deposit_percent !== undefined && payload.booking_deposit_percent !== "") {
        payload.booking_deposit_percent = Math.max(1, Math.min(100, parseFloat(payload.booking_deposit_percent) || 25));
      }
      const { data } = await api.post("/admin/ngo-profile", payload);
      setForm(data);
      toast.success("Company profile saved");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const isCompany = (form.company_type || "company") === "company";
  return (
    <div data-testid="admin-ngo-profile">
      <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-3">// System · Organisation profile</div>
      <h1 className="font-display text-4xl font-black uppercase tracking-tighter leading-none mb-2">
        {isCompany ? "Company Profile." : "NGO Profile."}
      </h1>
      <p className="text-sm text-white/60 mb-8 max-w-2xl">
        These details appear on every invoice, receipt, and the site footer. Switching entity type instantly rebrands
        the site and toggles GST on new invoices.
      </p>

      <div className="max-w-4xl space-y-6" data-testid="ngo-form">
        {SECTIONS.map((sec) => {
          if (sec.showIf && !sec.showIf(form)) return null;
          return (
            <div key={sec.title} className="bg-[#131A2B] border border-white/12 p-6">
              <div className="font-mono text-[10px] uppercase tracking-widest brand-blue mb-4">// {sec.title}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {sec.fields.map((f) => {
                  if (f.showIf && !f.showIf(form)) return null;
                  const value = form[f.k] ?? "";
                  return (
                    <div key={f.k} className={f.wide ? "md:col-span-2" : ""}>
                      <label className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1.5">{f.label}</label>
                      {f.options ? (
                        <select value={value} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                          data-testid={`ngo-${f.k}`}
                          className="w-full bg-black/40 border border-white/15 py-2.5 px-3 text-sm">
                          {f.options.map((o) => <option key={o.v} value={o.v} className="bg-[#0B1120]">{o.l}</option>)}
                        </select>
                      ) : f.type === "toggle" ? (
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={!!value}
                            onChange={(e) => setForm({ ...form, [f.k]: e.target.checked })}
                            data-testid={`ngo-${f.k}`} className="accent-brand-orange" />
                          <span className="text-sm text-white/70">Yes</span>
                        </label>
                      ) : (
                        <input type={f.type || "text"} value={value}
                          onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                          placeholder={f.placeholder} data-testid={`ngo-${f.k}`}
                          className="w-full bg-black/40 border border-white/15 focus:border-brand-blue outline-none px-3 py-2.5 text-sm" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Live GST preview — shows what a guest will actually see for a sample ₹1,000 base */}
        {isCompany && (
          <div className="bg-brand-blue/5 border border-brand-blue/30 p-6" data-testid="gst-preview">
            <div className="font-mono text-[10px] uppercase tracking-widest brand-blue mb-3">// Live GST preview</div>
            {(() => {
              const rate = parseFloat(form.gst_rate) || 18;
              const base = 1000;
              const withGst = Math.round(base * (1 + rate / 100));
              const inclusive = !!form.gst_inclusive;
              const displayed = inclusive ? base : base;   // in both modes admins enter/store one number
              const paidByGuest = inclusive ? base : withGst;
              const backCalcBase = inclusive ? Math.round(base / (1 + rate / 100)) : base;
              const backCalcTax = inclusive ? base - backCalcBase : Math.round((base * rate) / 100);
              return (
                <>
                  <p className="text-xs text-white/60 mb-4">
                    Sample: you store a price of <span className="brand-blue font-mono">₹{base.toLocaleString()}</span> for a service.
                    Below is exactly what a guest will see and what will appear on the invoice.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className={`border p-4 ${inclusive ? "border-brand-orange bg-brand-orange/5" : "border-white/12"}`}>
                      <div className="font-mono text-[10px] uppercase tracking-widest brand-orange">Inclusive mode {inclusive && "· SELECTED"}</div>
                      <div className="font-display text-2xl font-black mt-1">Guest pays ₹{base.toLocaleString()}</div>
                      <div className="text-[11px] font-mono text-white/50 mt-2">
                        Invoice back-calc: Base ₹{backCalcBase.toLocaleString()} · GST {rate}% ₹{backCalcTax}
                      </div>
                    </div>
                    <div className={`border p-4 ${!inclusive ? "border-brand-blue bg-brand-blue/5" : "border-white/12"}`}>
                      <div className="font-mono text-[10px] uppercase tracking-widest brand-blue">Exclusive mode {!inclusive && "· SELECTED"}</div>
                      <div className="font-display text-2xl font-black mt-1">Guest pays ₹{withGst.toLocaleString()}</div>
                      <div className="text-[11px] font-mono text-white/50 mt-2">
                        Base ₹{base.toLocaleString()} + GST {rate}% ₹{Math.round((base * rate) / 100)}
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 text-[10px] font-mono text-white/40" data-testid="gst-preview-note">
                    Refundable amounts (wallet top-up, security deposit) are NEVER taxed — regardless of this setting.
                  </div>
                </>
              );
            })()}
          </div>
        )}

        <button onClick={save} disabled={saving} data-testid="ngo-save"
          className="bg-brand-orange text-black px-6 py-3 font-bold text-xs uppercase tracking-widest hover:opacity-90 disabled:opacity-50 flex items-center gap-2">
          <Building2 size={14} /> {saving ? "Saving…" : "Save Organisation Profile"}
        </button>
      </div>
    </div>
  );
}
